import React, { useState, useEffect, useRef } from "react";
import { Box, Chip, TextField } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import katex from "katex";
import "katex/dist/katex.min.css";
import { v4 as uuidv4 } from "uuid";
import parse from "html-react-parser";
import MathRendererContent from "./MathRendererContent";
import AutoResizeTextField from "./AutoResizeTextField";
import { isNumeric } from "../../common/Utils";

const MathRenderer = ({
  content,
  questionId = "unknown",
  id,
  assessmentId,
  type,
  availableItems,
  // New props for centralized answer management
  answers = {},
  onAnswerChange = () => {},
}) => {
  // Remove local answers state - use props instead
  const [dragDropValues, setDragDropValues] = useState({});
  // State để track những items đã được sử dụng
  const [usedItems, setUsedItems] = useState([]);
  // Load dragDropValues và usedItems từ answers prop cho câu hỏi hiện tại
  useEffect(() => {
    if (type === "KT") {
      const currentQuestionAnswers = answers[questionId];
      const newDragDropValues = {};
      const newUsedItems = [];

      // Chỉ xử lý nếu có answers và availableItems
      if (currentQuestionAnswers && availableItems?.length > 0) {
        // Duyệt qua tất cả slots trong answers
        Object.entries(currentQuestionAnswers).forEach(([slotKey, itemId]) => {
          if (slotKey.startsWith("slot") && itemId) {
            // Extract slot number từ slotKey (e.g., "slot1" → "1")
            const slotNumber = slotKey.replace("slot", "");

            // Tìm item data để lấy content
            const itemData = availableItems.find((item) => item.id === itemId);
            if (itemData) {
              newDragDropValues[slotNumber] = itemData.content;
              newUsedItems.push(itemId);
            }
          }
        });
      }

      // Luôn set state để đảm bảo reset khi chuyển câu hỏi
      setDragDropValues(newDragDropValues);
      setUsedItems(newUsedItems);
    }
  }, [questionId, answers, availableItems, type]);

  // Remove auto-save logic - let parent handle this

  // Handle input change
  const handleInputChange = (inputId, value) => {
    // Parse number if possible, otherwise keep as string
    if (type === "TLN") {
      if (typeof value === "string" && value.includes(",")) {
        value = value.replace(",", ".");
      }

      onAnswerChange(questionId, value);
    } else if (type === "TLN_M") {
      // Parse number if possible, otherwise keep as string

      if (typeof value === "string" && value.includes(",")) {
        value = value.replace(",", ".");
      }

      const currentAnswer = answers[questionId] || {};
      const fieldKey = `${inputId?.split("-")[1]}.`;

      onAnswerChange(questionId, {
        ...currentAnswer,
        [fieldKey]: value,
      });
    }
  };

  // Handle drag end for react-beautiful-dnd
  const handleOnDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // Nếu không có destination (dropped outside)
    if (!destination) return;

    // Kiểm tra loại drag: từ available items hay từ placed items
    const isFromAvailableItems = source.droppableId === "available-items";
    const isFromDropZone = source.droppableId.startsWith("drop-zone-");
    const isPlacedItem = draggableId.startsWith("placed-");

    // Nếu drop vào drop zone
    if (destination.droppableId.startsWith("drop-zone-")) {
      const dropId = destination.droppableId.replace("drop-zone-", "");
      const slotKey = `slot${dropId}`;

      if (isFromAvailableItems) {
        // **Case 1: Drag từ available items → drop zone**
        const draggedItemData = availableItems.find(
          (item) => item.id === draggableId
        );

        if (draggedItemData) {
          // Kiểm tra nếu drop zone đã có item, restore về available pool
          const currentItemId = answers[questionId]?.[slotKey];
          if (currentItemId) {
            setUsedItems((prev) =>
              prev.filter((usedId) => usedId !== currentItemId)
            );
          }

          // Cập nhật với item mới
          setDragDropValues((prev) => ({
            ...prev,
            [dropId]: draggedItemData.content,
          }));

          setUsedItems((prev) => {
            if (!prev.includes(draggableId)) {
              return [...prev, draggableId];
            }
            return prev;
          });

          // Update answers through parent callback
          const currentAnswer = answers[questionId] || {};
          onAnswerChange(questionId, {
            ...currentAnswer,
            [slotKey]: draggableId,
          });
        }
      } else if (isFromDropZone && isPlacedItem) {
        // **Case 2: Drag giữa drop zones → SWAP**
        const sourceDropId = source.droppableId.replace("drop-zone-", "");
        const sourceSlotKey = `slot${sourceDropId}`;

        // Extract actual item ID từ placed item ID
        const actualItemId = draggableId.replace(`placed-`, "").split("-")[0];

        // Get current target item
        const targetItemId = answers[questionId]?.[slotKey];
        const targetItemData = availableItems.find(
          (item) => item.id === targetItemId
        );
        const sourceItemData = availableItems.find(
          (item) => item.id === actualItemId
        );

        if (targetItemId && targetItemData && sourceItemData) {
          // Swap dragDropValues
          setDragDropValues((prev) => ({
            ...prev,
            [sourceDropId]: targetItemData.content,
            [dropId]: sourceItemData.content,
          }));

          // Swap answers through parent callback
          const currentAnswer = answers[questionId] || {};
          onAnswerChange(questionId, {
            ...currentAnswer,
            [sourceSlotKey]: targetItemId,
            [slotKey]: actualItemId,
          });

          // usedItems không thay đổi vì chỉ swap
        } else {
          // Clear source
          setDragDropValues((prev) => {
            const newValues = { ...prev };
            delete newValues[sourceDropId];
            newValues[dropId] = sourceItemData.content;
            return newValues;
          });

          // Update answers through parent callback
          const currentAnswer = answers[questionId] || {};
          onAnswerChange(questionId, {
            ...currentAnswer,
            [sourceSlotKey]: undefined,
            [slotKey]: actualItemId,
          });
        }
      }
    }
    // Nếu drop trở lại available-items
    else if (destination.droppableId === "available-items") {
      if (isPlacedItem) {
        // Extract actual item ID
        const actualItemId = draggableId.replace(`placed-`, "").split("-")[0];
        const sourceDropId = source.droppableId.replace("drop-zone-", "");
        const sourceSlotKey = `slot${sourceDropId}`;

        // Clear source drop zone
        setDragDropValues((prev) => {
          const newValues = { ...prev };
          delete newValues[sourceDropId];
          return newValues;
        });

        // Clear source answer through parent callback
        const currentAnswer = answers[questionId] || {};
        onAnswerChange(questionId, {
          ...currentAnswer,
          [sourceSlotKey]: undefined,
        });

        // Remove from used items
        setUsedItems((prev) =>
          prev.filter((usedId) => usedId !== actualItemId)
        );
      }
    }
  };

  // Draggable Item Component for react-beautiful-dnd
  const DraggableItem = ({ id, children, index }) => {
    return (
      <Draggable draggableId={id} index={index}>
        {(provided, snapshot) => (
          <Chip
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            label={children}
            variant="outlined"
            color="primary"
            sx={{
              margin: 0.5,
              color: "black",
              backgroundColor: "#e8e8e8",
              cursor: snapshot.isDragging ? "grabbing" : "grab",
              transform: snapshot.isDragging ? "rotate(5deg)" : "none",
              opacity: snapshot.isDragging ? 0.8 : 1,
              border: "1px solid #e8e8e8", // Viền liền màu primary
              borderRadius: "5px", // Bo góc như Chip chuẩn
              // "&:hover": {
              //   backgroundColor: "#e8e8e8",
              //   color: "white",
              //   borderColor: "primary.main",
              // },
            }}
          />
        )}
      </Draggable>
    );
  };

  // Drop Zone Component for react-beautiful-dnd
  const DropZone = ({ id }) => {
    const currentValue = dragDropValues[id];
    const slotKey = `slot${id}`;
    const currentItemId = answers[questionId]?.[slotKey];

    const handleDelete = () => {
      // Tìm item ID từ answers để remove khỏi usedItems
      if (currentItemId) {
        setUsedItems((prev) =>
          prev.filter((usedId) => usedId !== currentItemId)
        );
      }

      // Xóa khỏi dragDropValues
      setDragDropValues((prev) => {
        const newValues = { ...prev };
        delete newValues[id];
        return newValues;
      });

      // Xóa khỏi answers through parent callback
      const currentAnswer = answers[questionId] || {};
      onAnswerChange(questionId, {
        ...currentAnswer,
        [slotKey]: undefined,
      });
    };

    return (
      <Droppable droppableId={`drop-zone-${id}`}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              minWidth: "120px",
              minHeight: "40px",
              border: "2px solid #e8e8e8",
              // borderColor: snapshot.isDraggingOver
              //   ? "primary.main"
              //   : currentValue
              //   ? "success.main"
              //   : "grey.400",
              borderRadius: 2,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              margin: 0.5,
              padding: 0,
              // backgroundColor: snapshot.isDraggingOver
              //   ? "primary.light"
              //   : currentValue
              //   ? "success.light"
              //   : "grey.50",
              transition: "all 0.2s ease",
              // "&:hover": {
              //   borderColor: "primary.main",
              //   backgroundColor: "primary.light",
              // },
            }}
          >
            {currentValue && currentItemId ? (
              // Wrap Chip trong Draggable với unique ID để có thể kéo giữa drop zones
              <Draggable
                draggableId={`placed-${currentItemId}-${id}`}
                index={0}
              >
                {(dragProvided, dragSnapshot) => (
                  <Chip
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    label={<MathRendererContent content={currentValue} />}
                    // color="success"
                    // variant="filled"
                    // onDelete={(e) => {
                    //   e.stopPropagation();
                    //   handleDelete();
                    // }}
                    sx={{
                      color: "black",
                      borderRadius: 1,
                      width: "100%",
                      height: "100%",
                      minWidth: "140px",
                      minHeight: "40px",
                      cursor: dragSnapshot.isDragging ? "grabbing" : "grab",
                      transform: dragSnapshot.isDragging
                        ? "rotate(3deg)"
                        : "none",
                      opacity: dragSnapshot.isDragging ? 0.7 : 1,
                      "& .MuiChip-label": {
                        padding: "8px 12px",
                        fontSize: "14px",
                        fontWeight: "500",
                      },
                      "&:hover": {
                        transform: dragSnapshot.isDragging
                          ? "rotate(3deg)"
                          : "scale(1.02)",
                      },
                      "& .MuiChip-deleteIcon": {
                        cursor: "pointer",
                        "&:hover": {
                          color: "error.main",
                        },
                      },
                    }}
                  />
                )}
              </Draggable>
            ) : (
              <Box sx={{ color: "grey.600", fontSize: "0.875rem" }}>
                {/* {snapshot.isDraggingOver ? "Thả tại đây" : "Kéo thả vào đây"} */}
              </Box>
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    );
  };

  // Helper function để tạo Drop Zone
  const createDropZone = (dropId) => {
    return <DropZone key={dropId} id={dropId} />;
  };
  // Helper function để tạo MUI TextField
  const createTextField = (blankId) => {
    const inputId = `${questionId}-${blankId}`;
    let currentValue = "";
    if (type === "TLN") {
      currentValue = answers[questionId] || "";
    } else if (type === "TLN_M") {
      currentValue = answers[questionId]?.[`${inputId?.split("-")[1]}.`] || "";
    }

    return (
      <AutoResizeTextField
        inputId={inputId}
        value={currentValue}
        onChange={handleInputChange}
      />
    );
  };
  const parseContent = (text) => {
    if (!text) return [];

    // Kiểm tra nếu content chứa HTML tags
    const hasHtmlTags = /<[^>]*>/g.test(text);

    if (hasHtmlTags) {
      // Xử lý content có HTML formatting
      return parseHtmlContent(text);
    } else {
      // Xử lý content thông thường (chỉ có text và LaTeX)
      return parseTextContent(text);
    }
  };

  // Function để xử lý fill-in-the-blank placeholders
  const processFillInTheBlanks = (htmlText) => {
    // Tìm và thay thế các placeholder fill-blank bằng placeholder cho React components
    const fillBlankPattern = /____FILL_BLANK_(\d+)____/g;

    return htmlText.replace(fillBlankPattern, (match, blankId) => {
      // Tạo placeholder đặc biệt để sau này thay thế bằng React TextField component
      return `<span class="textfield-placeholder" data-blank-id="${blankId}"></span>`;
    });
  };

  // Function để xử lý drag-drop placeholders
  const processDragDropBlanks = (htmlText) => {
    // Tìm và thay thế các placeholder drag-drop bằng placeholder cho React components
    const dragDropPattern = /____DRAG_DROP_(\d+)____/g;

    const result = htmlText.replace(dragDropPattern, (match, dropId) => {
      // Tạo placeholder đặc biệt để sau này thay thế bằng React DropZone component
      return `<span class="dropzone-placeholder" data-drop-id="${dropId}"></span>`;
    });

    return result;
  };

  const parseHtmlContent = (htmlText) => {
    // Xử lý fill-in-the-blank placeholders trước
    htmlText = processFillInTheBlanks(htmlText);
    // Sau đó xử lý drag-drop placeholders
    htmlText = processDragDropBlanks(htmlText);

    // Thay thế các ký hiệu latex block như \[...\] bằng $...$
    htmlText = htmlText.replaceAll("\\[", "$");
    htmlText = htmlText.replaceAll("\\]", "$");

    // Clean up br tags ở đầu và cuối
    htmlText = htmlText.replace(/^(\s*<br\s*\/?>)+/gi, "");
    htmlText = htmlText.replace(/(\s*<br\s*\/?>)+$/gi, "");

    // Remove multiple consecutive br tags
    htmlText = htmlText.replace(/(<br\s*\/?>){3,}/gi, "<br/><br/>");

    // Xử lý bullet points và list items đặc biệt
    htmlText = htmlText.replaceAll(/•/g, "•"); // Preserve bullet points
    htmlText = htmlText.replaceAll(/◦/g, "◦"); // White bullet
    htmlText = htmlText.replaceAll(/▪/g, "▪"); // Black small square
    htmlText = htmlText.replaceAll(/▫/g, "▫"); // White small square

    // Regex để tìm tất cả các công thức toán học $...$
    const regex = /\$(.*?)\$/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(htmlText)) !== null) {
      // Đẩy phần HTML trước công thức vào mảng parts
      if (match.index > lastIndex) {
        const htmlPart = htmlText.substring(lastIndex, match.index);
        // Thêm vào nội dung đáp án nếu không phải dòng trống
        if (htmlPart.trim()) {
          try {
            // Kiểm tra nếu có TextField placeholder
            if (htmlPart.includes('class="textfield-placeholder"')) {
              // Parse HTML và thay thế placeholder bằng MUI TextField
              const parsedContent = parse(htmlPart, {
                replace: (domNode) => {
                  if (
                    domNode.attribs &&
                    domNode.attribs.class === "textfield-placeholder"
                  ) {
                    const blankId = domNode.attribs["data-blank-id"];
                    return createTextField(blankId);
                  }
                  return domNode;
                },
              });
              parts.push(parsedContent);
            } else if (htmlPart.includes('class="dropzone-placeholder"')) {
              // Parse HTML và thay thế placeholder bằng DropZone
              const parsedContent = parse(htmlPart, {
                replace: (domNode) => {
                  if (
                    domNode.attribs &&
                    domNode.attribs.class === "dropzone-placeholder"
                  ) {
                    const dropId = domNode.attribs["data-drop-id"];
                    return createDropZone(dropId);
                  }
                  return domNode;
                },
              });
              parts.push(parsedContent);
            } else if (htmlPart.includes("<") && htmlPart.includes(">")) {
              // Sử dụng dangerouslySetInnerHTML cho các HTML content khác
              parts.push(
                <span
                  key={uuidv4()}
                  dangerouslySetInnerHTML={{ __html: htmlPart }}
                />
              );
            } else {
              // Plain text, add directly
              parts.push(htmlPart);
            }
          } catch (error) {
            console.error("Error parsing HTML:", error);
            // Fallback: thêm text thuần
            parts.push(htmlPart);
          }
        }
      }

      // Xử lý công thức toán học
      let formula = match[1];
      formula = processLatexFormula(formula);

      if (/[à-ỹ]/i.test(formula)) {
        parts.push(formula);
      } else {
        try {
          const html = katex.renderToString(formula, {
            throwOnError: false,
          });
          parts.push(
            <span key={uuidv4()} dangerouslySetInnerHTML={{ __html: html }} />
          );
        } catch (error) {
          console.error("Error rendering LaTeX:", error);
          parts.push(formula); // Fallback to plain text
        }
      }
      lastIndex = regex.lastIndex;
    }

    // Đẩy phần HTML cuối cùng vào mảng parts
    if (lastIndex < htmlText?.length) {
      const remainingHtml = htmlText.substring(lastIndex);
      if (remainingHtml.trim()) {
        try {
          // Kiểm tra nếu có TextField placeholder trong remaining HTML
          if (remainingHtml.includes('class="textfield-placeholder"')) {
            // Parse HTML và thay thế placeholder bằng MUI TextField
            const parsedContent = parse(remainingHtml, {
              replace: (domNode) => {
                if (
                  domNode.attribs &&
                  domNode.attribs.class === "textfield-placeholder"
                ) {
                  const blankId = domNode.attribs["data-blank-id"];
                  return createTextField(blankId);
                }
                return domNode;
              },
            });
            parts.push(parsedContent);
          } else if (remainingHtml.includes('class="dropzone-placeholder"')) {
            // Parse HTML và thay thế placeholder bằng DropZone
            const parsedContent = parse(remainingHtml, {
              replace: (domNode) => {
                if (
                  domNode.attribs &&
                  domNode.attribs.class === "dropzone-placeholder"
                ) {
                  const dropId = domNode.attribs["data-drop-id"];
                  return createDropZone(dropId);
                }
                return domNode;
              },
            });
            parts.push(parsedContent);
          } else if (
            remainingHtml.includes("<") &&
            remainingHtml.includes(">")
          ) {
            // Sử dụng dangerouslySetInnerHTML cho remaining HTML khác
            parts.push(
              <span
                key={uuidv4()}
                dangerouslySetInnerHTML={{ __html: remainingHtml }}
              />
            );
          } else {
            parts.push(remainingHtml);
          }
        } catch (error) {
          console.error("Error parsing remaining HTML:", error);
          parts.push(remainingHtml);
        }
      }
    }

    return parts;
  };

  const parseTextContent = (text) => {
    // Thay thế các ký hiệu latex block như \[...\] bằng $...$
    text = text.replaceAll("\\[", "$");
    text = text.replaceAll("\\]", "$");

    // Đảm bảo xuống dòng bằng cách thay các ký hiệu xuống dòng (\n) bằng <br />
    text = text.replaceAll(/\n/g, "<br/>");
    // Regex để tìm tất cả các công thức toán học $...$
    const regex = /\$(.*?)\$/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Đẩy phần text trước công thức vào mảng parts
      if (match.index > lastIndex) {
        const plainText = text.substring(lastIndex, match.index);
        if (plainText === "<br/>") {
          parts.push(<br key={`line-${uuidv4()}`} />);
        } else if (plainText?.includes("<br/>")) {
          const plainTextArray = plainText?.split("<br/>");
          plainTextArray.forEach((line, index) => {
            if (plainTextArray.length === 1) {
              if (plainText.startsWith("<br/>")) {
                parts.push(<br key={`line-${uuidv4()}`} />);
                parts.push(line); // Đẩy nội dung vào parts
              } else if (plainText.endsWith("<br/>")) {
                parts.push(line); // Đẩy nội dung vào parts
                parts.push(<br key={`line-${uuidv4()}`} />);
              }
            } else {
              parts.push(line); // Đẩy nội dung vào parts
              if (index !== plainTextArray.length - 1) {
                parts.push(<br key={`line-${uuidv4()}`} />);
              }
            }
          });
        } else {
          parts.push(plainText);
        }
      }

      // Lấy công thức toán học từ match và loại bỏ dấu $
      let formula = match[1];
      formula = processLatexFormula(formula);

      // Render công thức bằng KaTeX và đẩy vào mảng parts
      if (/[à-ỹ]/i.test(formula)) {
        parts.push(formula);
      } else {
        const html = katex.renderToString(formula, {
          throwOnError: false,
        });
        parts.push(
          <span key={uuidv4()} dangerouslySetInnerHTML={{ __html: html }} />
        );
      }
      lastIndex = regex.lastIndex;
    }

    // Đẩy phần text cuối cùng (sau công thức cuối cùng) vào mảng parts
    if (lastIndex < text?.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText === "<br/>") {
        parts.push(<br key={`line-${uuidv4()}`} />);
      } else if (remainingText?.includes("<br/>")) {
        const remainingTextArray = remainingText?.split("<br/>");
        remainingTextArray.forEach((line, index) => {
          if (remainingTextArray.length === 1) {
            if (remainingText.startsWith("<br/>")) {
              parts.push(<br key={`line-${uuidv4()}`} />);
              parts.push(line); // Đẩy nội dung vào parts
            } else if (remainingText.endsWith("<br/>")) {
              parts.push(line); // Đẩy nội dung vào parts
              parts.push(<br key={`line-${uuidv4()}`} />);
            }
          } else {
            if (line.trim() !== "") {
              parts.push(line); // Đẩy nội dung vào parts
            }
            if (index !== remainingTextArray.length - 1) {
              parts.push(<br key={`line-${uuidv4()}`} />); // Thêm <br /> sau mỗi dòng
            }
          }
        });
      } else {
        parts.push(remainingText);
      }
    }

    return parts;
  };

  const processLatexFormula = (formula) => {
    const pattern = /\\begin\{array\}\{\*\{20\}\{l\}\}/g;

    const matches = formula.match(pattern);
    if (
      matches &&
      matches.length < 2 &&
      formula.includes("\\begin{array}{*{20}{l}}")
    ) {
      formula = formula.replaceAll(
        "end{array} \\Leftrightarrow",
        "end{array} \\right. \\Leftrightarrow"
      );

      formula = formula.replaceAll(
        "end{array} \\Rightarrow",
        "end{array} \\right. \\Rightarrow"
      );

      formula = formula.replaceAll(
        "end{array}} \\right.} \\right.",
        "end{array} \\right. "
      );
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{l}}",
        "\\begin{array}{l}"
      );

      formula = formula.replaceAll("end{array}}", "end{array}");
    } else if (
      formula.includes("\\begin{array}{*{20}{l}}") ||
      formula.includes("{\\begin{array}{*{20}{c}}") ||
      formula.includes("{\\begin{array}{*{20}{r}}")
    ) {
      formula = formula.replaceAll("\\left\\{", "");
      formula = formula.replaceAll(/\\left\[/g, "");
      formula = formula.replaceAll("} \\right.}", "");
      formula = formula.replaceAll("\\right.}", "");
      formula = formula.replaceAll(/\\right\./g, "");
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{l}}",
        "\\begin{cases}"
      );
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{c}}",
        "\\begin{cases}"
      );
      formula = formula.replaceAll(
        "{\\begin{array}{*{20}{r}}",
        "\\begin{cases}"
      );
      formula = formula.replaceAll("\\begin{array}{l}", "\\begin{cases}");
      formula = formula.replaceAll("end{array}}", "end{cases}");
      formula = formula.replaceAll("\\end{array}", "\\end{cases}");
      formula = formula.replaceAll(
        "{\\rm{ suy ra }}",
        "\\quad \\Rightarrow \\quad"
      );
    }

    // Render công thức bằng KaTeX và đẩy vào mảng parts
    formula = formula
      .replaceAll("<br/>", "")
      .replaceAll("<p>", "")
      .replaceAll("</p>", "")
      .replaceAll("^^\\circ", "^\\circ")
      .replaceAll("&lt;", "<")
      .replaceAll("&gt;", ">")
      .replaceAll("\\mathop \\smallint \\nolimits^", "\\int")
      .replaceAll("\\right)}^", "\\right)}")
      // .replaceAll("\\right)^", "\\right)")
      .replaceAll("C)^'}", "C)'}")

    return formula;
  };

  return type !== "KT" ? (
    <div
      style={{
        // Base styles để đảm bảo formatting hoạt động
        lineHeight: "1.6",
      }}
    >
      <style>
        {`
         /* Reset để tránh conflicts */
         .math-renderer * {
           list-style: none;
         }
         
         /* CSS cho các elements được render bằng dangerouslySetInnerHTML */
         .math-renderer strong, .math-renderer b { font-weight: bold; color: black; }
         .math-renderer em, .math-renderer i { font-style: italic; }
         .math-renderer u { text-decoration: underline; }
         .math-renderer del { text-decoration: line-through; }
         .math-renderer sub { vertical-align: sub; font-size: smaller; }
         .math-renderer sup { vertical-align: super; font-size: smaller; }
         .math-renderer mark { background-color: #fef08a; padding: 1px 3px; }
         
         /* CSS cho lists với bullet points - Override reset */
         .math-renderer ul { 
           margin-left: 20px; 
           padding-left: 0;
           list-style-type: disc !important;
           list-style-position: outside !important;
         }
         .math-renderer ol { 
           margin-left: 20px; 
           padding-left: 0;
           list-style-type: decimal !important;
           list-style-position: outside !important;
         }
         .math-renderer li { 
           margin: 4px 0; 
           display: list-item !important;
           list-style-type: inherit !important;
           list-style-position: inherit !important;
           padding-left: 4px;
         }
                   /* CSS cho tables từ Word */
          .math-renderer .word-table {
            border-collapse: collapse !important;
            margin: 10px auto !important;
            border: 2px solid #333 !important;
            width: auto !important;
            min-width: 200px !important;
            max-width: 100% !important;
            font-family: inherit !important;
          }
          
          .math-renderer .word-table-cell {
            border: 1px solid #333 !important;
            padding: 8px 12px !important;
            text-align: center !important;
            background-color: #f9f9f9 !important;
            vertical-align: middle !important;
          }
          
          .math-renderer .word-table-header {
            border: 2px solid #333 !important;
            padding: 8px 12px !important;
            text-align: center !important;
            background-color: #d0d0d0 !important;
            font-weight: bold !important;
            vertical-align: middle !important;
          }
          
          .math-renderer .word-table-row {
            border: 1px solid #333 !important;
          }

          .math-renderer .word-table-body {
            border: inherit !important;
          }

          .math-renderer .word-table-head {
            border: inherit !important;
            background-color: #d0d0d0 !important;
          }

          /* Fallback cho table chung */
          table, th, td {
           border: 1px solid;
           padding: 5px;
          }

          /* Responsive table */
          @media (max-width: 768px) {
            .math-renderer .word-table {
              font-size: 12px !important;
              min-width: 150px !important;
            }
            
            .math-renderer .word-table-cell,
            .math-renderer .word-table-header {
              padding: 4px 6px !important;
            }
          }`}
      </style>
      <div className="math-renderer">
        {parseContent(content)
          ?.map((part, index) => {
            // Safety check để đảm bảo part là valid React child
            if (part === null || part === undefined) {
              return null;
            }

            // Nếu part là object có properties DOM node, skip nó
            if (typeof part === "object" && part.type && part.parent) {
              console.warn("Skipping invalid DOM node:", part);
              return null;
            }

            // Nếu part là string hoặc number, render trực tiếp
            if (typeof part === "string" || typeof part === "number") {
              return <span key={index}>{part}</span>;
            }

            // Nếu part là React element, render normal
            return <React.Fragment key={index}>{part}</React.Fragment>;
          })
          .filter(Boolean)}
      </div>

      <style>
        {`
          .math-renderer .MuiTextField-root {
            margin: 4px 8px !important; /* Đảm bảo spacing đều */
          }
          
          .math-renderer .MuiTextField-root + .MuiTextField-root {
            margin-left: 12px !important; /* Extra spacing giữa các TextField liền kề */
          }
          
          /* Responsive spacing */
          @media (max-width: 768px) {
            .math-renderer .MuiTextField-root {
              margin: 2px 4px !important;
              min-width: 120px !important;
            }
          }
          
          /* Better vertical alignment */
          .math-renderer {
            line-height: 2.2 !important;
          }
          
          .math-renderer .MuiTextField-root {
            vertical-align: baseline !important;
          }
        `}
      </style>
    </div>
  ) : (
    <DragDropContext onDragEnd={handleOnDragEnd}>
      <div
        style={{
          // Base styles để đảm bảo formatting hoạt động
          lineHeight: "1.6",
        }}
      >
        {/* Available items for dragging */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 1, fontWeight: "bold", fontSize: "0.875rem" }}>
            Kéo ô thích hợp và thả vào vị trí tương ứng
          </Box>
          <Droppable droppableId="available-items" direction="horizontal">
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  minHeight: "50px",
                  backgroundColor: "transparent",
                  border: "1px solid #1976d2",
                  borderRadius: "5px",
                  alignItems: "center",
                  padding: 0,
                  paddingLeft: "10px",
                }}
              >
                {availableItems && availableItems.length > 0
                  ? availableItems
                      .filter((item) => !usedItems.includes(item.id))
                      .map((item, index) => (
                        <DraggableItem
                          key={item?.id}
                          id={item?.id}
                          index={index}
                        >
                          <MathRendererContent content={item?.content} />
                        </DraggableItem>
                      ))
                  : null}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Box>

        <style>
          {`
           /* Reset để tránh conflicts */
           .math-renderer * {
             list-style: none;
           }
           
           /* CSS cho các elements được render bằng dangerouslySetInnerHTML */
           .math-renderer strong, .math-renderer b { font-weight: bold; color: black;}
           .math-renderer em, .math-renderer i { font-style: italic; }
           .math-renderer u { text-decoration: underline; }
           .math-renderer del { text-decoration: line-through; }
           .math-renderer sub { vertical-align: sub; font-size: smaller; }
           .math-renderer sup { vertical-align: super; font-size: smaller; }
           .math-renderer mark { background-color: #fef08a; padding: 1px 3px; }
           
           /* CSS cho lists với bullet points - Override reset */
           .math-renderer ul { 
             margin-left: 20px; 
             padding-left: 0;
             list-style-type: disc !important;
             list-style-position: outside !important;
           }
           .math-renderer ol { 
             margin-left: 20px; 
             padding-left: 0;
             list-style-type: decimal !important;
             list-style-position: outside !important;
           }
           .math-renderer li { 
             margin: 4px 0; 
             display: list-item !important;
             list-style-type: inherit !important;
             list-style-position: inherit !important;
             padding-left: 4px;
           }
                     /* CSS cho tables từ Word */
            .math-renderer .word-table {
              border-collapse: collapse !important;
              margin: 10px auto !important;
              border: 2px solid #333 !important;
              width: auto !important;
              min-width: 200px !important;
              max-width: 100% !important;
              font-family: inherit !important;
            }
            
            .math-renderer .word-table-cell {
              border: 1px solid #333 !important;
              padding: 8px 12px !important;
              text-align: center !important;
              background-color: #f9f9f9 !important;
              vertical-align: middle !important;
            }
            
            .math-renderer .word-table-header {
              border: 2px solid #333 !important;
              padding: 8px 12px !important;
              text-align: center !important;
              background-color: #d0d0d0 !important;
              font-weight: bold !important;
              vertical-align: middle !important;
            }
            
            .math-renderer .word-table-row {
              border: 1px solid #333 !important;
            }

            .math-renderer .word-table-body {
              border: inherit !important;
            }

            .math-renderer .word-table-head {
              border: inherit !important;
              background-color: #d0d0d0 !important;
            }

            /* Fallback cho table chung */
            table, th, td {
             border: 1px solid;
             padding: 5px;
            }

            /* Responsive table */
            @media (max-width: 768px) {
              .math-renderer .word-table {
                font-size: 12px !important;
                min-width: 150px !important;
              }
              
              .math-renderer .word-table-cell,
              .math-renderer .word-table-header {
                padding: 4px 6px !important;
              }
            }`}
        </style>
        <div className="math-renderer">
          {parseContent(content)
            ?.map((part, index) => {
              // Safety check để đảm bảo part là valid React child
              if (part === null || part === undefined) {
                return null;
              }

              // Nếu part là object có properties DOM node, skip nó
              if (typeof part === "object" && part.type && part.parent) {
                console.warn("Skipping invalid DOM node:", part);
                return null;
              }

              // Nếu part là string hoặc number, render trực tiếp
              if (typeof part === "string" || typeof part === "number") {
                return <span key={index}>{part}</span>;
              }

              // Nếu part là React element, render normal
              return <React.Fragment key={index}>{part}</React.Fragment>;
            })
            .filter(Boolean)}
        </div>

        <style>
          {`
            .math-renderer .MuiTextField-root {
              margin: 4px 8px !important; /* Đảm bảo spacing đều */
            }
            
            .math-renderer .MuiTextField-root + .MuiTextField-root {
              margin-left: 12px !important; /* Extra spacing giữa các TextField liền kề */
            }
            
            /* Responsive spacing */
            @media (max-width: 768px) {
              .math-renderer .MuiTextField-root {
                margin: 2px 4px !important;
                min-width: 120px !important;
              }
            }
            
            /* Better vertical alignment */
            .math-renderer {
              line-height: 2.2 !important;
            }
            
            .math-renderer .MuiTextField-root {
              vertical-align: baseline !important;
            }
          `}
        </style>
      </div>
    </DragDropContext>
  );
};

export default MathRenderer;
